import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import Table from "common/graphs/Table/Table";
import { NumberCell } from "common/graphs/Table/TableRenders";
import { atBqnTarget } from "common/api";
import BqnContext from "common/BqnContext";
import Loading from "common/Loading";
import { atoiv6, atoiv4, durationToInt } from "common/utils";
import {
  openPolicyOrReport,
  formatMB,
} from "views/Status/Subscribers/Report/ListSubscribers";
import { openSubscriberDetails } from "views/Status/Subscribers/Report";
import {
  PERIOD_DASHBOARD_TO_TIME_PERIOD,
  RETURN_VIEW,
  LOAD_INTERVAL,
} from "./utils";
import useDashboardData from "./useDashboardData";

const Wrapper = styled.div`
  width: 100%;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  color: var(--text-primary);
`;

const getSubscribers = ({ mode, bqnId }) => {
  return ifCl
    .run(atBqnTarget("show subscriber all downlink lines 10", mode, bqnId))
    .then((response) => {
      const [head, ...rows] = response.trim().split("\n");
      const headerAttrs = head.trim().split(/\s+/);
      const parsedData = rows.map((row) => {
        const attrs = row.trim().split(/\s+/);
        const item = attrs.reduce((acc, attr, index) => {
          const attrName = headerAttrs[index];
          acc[attrName] = attr;
          return acc;
        }, {});
        return item;
      });
      return parsedData;
    });
};

const getColumns = ({ mode, period, hideRates, loginBqnId }) => {
  const timePeriod = PERIOD_DASHBOARD_TO_TIME_PERIOD[period];
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            title: "BQN ID",
            numeric: true,
          },
        ]
      : []),
    {
      label: "ADDR",
      id: "ADDR",
      cellClassName: "hyperlink-text",
      title: "Subscriber’s IP address",
      numeric: false,
      onClick: (addr) =>
        openSubscriberDetails({ addr }, timePeriod, RETURN_VIEW),
      parse: (value) => {
        if (!value) {
          return 0;
        }
        return value.includes(":") ? atoiv6(value) : atoiv4(value);
      },
      parseFilter: (value) => value,
    },
    {
      label: "SUBSCRIBER-ID",
      id: "SUBSCRIBER-ID",
      title: "Subscriber ID",
      cellClassName: "hyperlink-text",
      onClick: (subsId) => openSubscriberDetails({ subsId }, timePeriod, RETURN_VIEW),
      numeric: false,
    },
    {
      label: "BLOCK",
      id: "BLOCK",
      title: login.isPlatform("gs-kr")
        ? "Blocking from quota"
        : "Blocking from billing configuration or quota",
      numeric: false,
    },
    ...(hideRates
      ? [
          {
            label: "RATE-POLICY",
            id: "POLICY-RATE",
            title: "Name of the subscriber's rate police",
            numeric: false,
            onClick: null,
            cellClassName: "hyperlink-text",
          },
        ]
      : [
          {
            label: "RATE-POLICY",
            id: "POLICY-RATE",
            title: "Name of the subscriber's rate police",
            numeric: false,
            onClick: (policy, row) => {
              const rowBqnId = row["BQN"];
              openPolicyOrReport(
                policy,
                timePeriod,
                mode === "cluster"
                  ? isNaN(rowBqnId)
                    ? loginBqnId
                    : rowBqnId
                  : null,
                RETURN_VIEW,
              );
            },
            cellClassName: "hyperlink-text",
          },
        ]),
    {
      label: "MBYTES-UP",
      id: "BYTES-UPLINK",
      title:
        "Total up traffic volume of this subscriber since it became active",
      numeric: true,
      renderCell: (rawValue) => {
        const { value, precision } = formatMB(rawValue);
        return <NumberCell value={value} hasNA={true} precision={precision} />;
      },
    },
    {
      label: "MBYTES-DOWN",
      id: "BYTES-DOWNLINK",
      title:
        "Total down traffic volume of this subscriber since it became active",
      numeric: true,
      renderCell: (rawValue) => {
        const { value, precision } = formatMB(rawValue);
        return <NumberCell value={value} hasNA={true} precision={precision} />;
      },
    },
    {
      label: "FLOWS",
      id: "FL-ACTIVE",
      title:
        "Total number of currently active traffic flows (TCP connections, UDP or IP flows)",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
      numeric: true,
    },
    {
      label: "CURR-Mbps",
      id: "CURR-Mbps",
      title: "Current speed in Mbps",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      label: "MEAN-Mbps",
      id: "MEAN-Mbps",
      title:
        "Moving average of the mean speed (calculated every 10 minutes) in Mbps.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      id: "MAX-Mbps",
      label: "MAX-Mbps",
      title: "maximum speed in Mbps during the previous 24 hours",
      renderCell: (value) => (
        <NumberCell
          value={value}
          hasNA={true}
          precision={1}
          thousands={false}
        />
      ),
      numeric: true,
    },
    {
      label: "LIM-Mbps",
      id: "LIM-Mbps",
      hidden: true,
    },
    {
      id: "RTT-P80-ms",
      label: "RTT-P80-ms",
      title:
        "The 80-th percentile of all RTT latency, measured from BQN to end user, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      id: "RTT-MED-ms",
      label: "RTT-MED-ms",
      title:
        "The 50-th percentile (median) of all RTT latency samples, measured from BQN to end user, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      id: "RTT-MIN-ms",
      label: "RTT-MIN-ms",
      title:
        "The mean of all RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      id: "RTX",
      label: "RTX",
      title:
        "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} percent={true} />
      ),
      numeric: true,
    },
    {
      id: "MAX-SPEED",
      label: "MAX-SPEED-%",
      title:
        "Moving average of the percentage of traffic sent with a speed close to the maximum",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} percent={true} />
      ),
      numeric: true,
    },
    {
      id: "CONGEST",
      label: "CONGESTION",
      title: "Moving average of percentage of traffic suffering congestion",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} percent={true} />
      ),
      numeric: true,
    },
    {
      label: "WARN",
      title: "Number of warnings",
      id: "WARN",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={0} />
      ),
      parse: (value) => parseFloat(value),
    },
    {
      label: "LIFETIME",
      id: "LIFETIME",
      title: "Duration so far of this subscriber session",
      numeric: true,
      parse: (data) => (data === "n/a" || !data ? 0 : durationToInt(data)),
    },
  ];
};

function Subscribers({ period, profile }) {
  // The columns are those of the BQN of the session and of the mode of the
  // cluster, which the table needs and the retrieval does not.
  const {
    loginBqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const { hideRates = false } = profile || {};

  // The table has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data, isLoading } = useDashboardData({
    retrieve: getSubscribers,
    onError: "Error fetching subscribers data:",
    interval: LOAD_INTERVAL,
  });

  // Each column carries how to render and to sort its values, so the table
  // is given the same ones while nothing they depend on changes.
  const columns = useMemo(
    () => getColumns({ mode, period, hideRates, loginBqnId }),
    [mode, period, hideRates, loginBqnId],
  );

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!data || data.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <Table
        isSelectable={false}
        isRowSelectable={false}
        data={data}
        sort={true}
        columns={columns}
        rowsPerPageNr={50}
        exportCSV={false}
        hasColumnFilter={false}
        hasPagination={false}
        hasSelectNrRows={false}
        search={false}
        maxHeight={210}
      />
    </Wrapper>
  );
}

export default React.memo(Subscribers);
